/**
 * `ScoreGuide` — guida contestuale ai punteggi delle competenze mostrati in
 * Dashboard e nel Dettaglio competenza.
 *
 * Rende un piccolo pulsante con l'icona "info" che apre una `Modal` con la
 * spiegazione delle due metriche complementari (Copertura e Proficiency) e del
 * criterio di ordinamento. Replica il pattern di `LivelloGuide`: stesso trigger
 * circolare, stesso `Modal` del design system, stesso footer "Ho capito".
 *
 * È puramente presentazionale e con stato di apertura interno: il chiamante
 * deve solo posizionare il componente accanto al titolo della sezione.
 */
import { useState } from 'react';
import { Button, Icon, Modal } from './ui';

export interface ScoreGuideProps {
  /** Classe extra sul pulsante trigger (es. per l'allineamento nel titolo). */
  className?: string;
}

/** Voce della guida: metrica + descrizione estesa. */
interface GuideEntry {
  term: string;
  description: string;
}

/**
 * Testi di dominio della guida. Distinguono chiaramente **ampiezza**
 * (Copertura) da **profondità** (Proficiency) e spiegano perché l'ordine delle
 * card può non seguire la sola Copertura.
 */
const GUIDE_ENTRIES: readonly GuideEntry[] = [
  {
    term: 'Copertura',
    description:
      'Quante delle skill richieste dalla competenza hai già valutato — con almeno una Fonte di Livello (la tua autovalutazione, l’AI o le review) — pesate per la loro importanza. Misura l’ampiezza: conta quante skill possiedi, non quanto sei bravo. Una skill senza alcun Livello non è coperta.',
  },
  {
    term: 'Proficiency',
    description:
      'Lo Score_Skill medio sulle sole skill che hai valutato (pesato per importanza). Misura la profondità: le skill mancanti non la abbassano. Se non hai ancora valutato alcuna skill della competenza, non è calcolabile e appare come “—”.',
  },
  {
    term: 'Ranking',
    description:
      'Le competenze sono ordinate con un punteggio interno che combina Copertura e Proficiency e le pondera in base a quante skill hai valutato: un ottimo risultato basato su pochissime skill è meno affidabile e viene ridimensionato. Per questo l’ordine può non seguire esattamente la sola Copertura.',
  },
];

/**
 * Pulsante "info" + modale con la guida ai punteggi. Lo stato di apertura è
 * interno: il chiamante deve solo posizionare il componente.
 */
export function ScoreGuide({ className }: ScoreGuideProps) {
  const [open, setOpen] = useState(false);

  const triggerClasses = ['livello-guide__trigger', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <button
        type="button"
        className={triggerClasses}
        aria-label="Come funzionano i punteggi"
        title="Come funzionano i punteggi"
        onClick={() => setOpen(true)}
      >
        <Icon name="info" size={18} />
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Come funzionano i punteggi"
        footer={
          <Button type="button" variant="primary" onClick={() => setOpen(false)}>
            Ho capito
          </Button>
        }
      >
        <p className="livello-guide__intro">
          Ogni competenza ha due punteggi complementari: la Copertura dice
          quante skill possiedi, la Proficiency quanto sei bravo su quelle che
          possiedi. Guardarle insieme evita conclusioni fuorvianti.
        </p>
        <ul className="livello-guide__list">
          {GUIDE_ENTRIES.map((entry) => (
            <li key={entry.term} className="livello-guide__item">
              <span className="livello-guide__badge">
                <span className="livello-guide__label">{entry.term}</span>
              </span>
              <span className="livello-guide__desc">{entry.description}</span>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}

export default ScoreGuide;
